/**
 * Reports — global, cross-property reports list.
 *
 * Filters: kind (Crawl / Traffic / Backlinks / Audit / All) + property
 * dropdown. Per-row Kind badge + per-row property link + Source link
 * back to the underlying data view (Crawl detail / GSC / Backlinks).
 *
 * For per-property report management use /p/:id/reports — this page
 * shows the cross-property roll-up (latest activity, every customer at
 * once). Both pages share useReports() with different filters.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  Download, FileText, Globe, Link as LinkIcon, Search, Wrench,
  ExternalLink, Building2,
} from "lucide-react";
import {
  useReports, useProperties, reportDownloadUrl,
  type Report, type ReportKind, type Property,
} from "@/lib/queries";
import { cn } from "@/lib/utils";

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtBytes(n: number | null): string {
  if (n === null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}


const KIND_META: Record<
  ReportKind | "all",
  { label: string; Icon: React.ComponentType<{ className?: string }>; tone: string }
> = {
  all:        { label: "All",       Icon: FileText,  tone: "" },
  crawl:      { label: "Crawl",     Icon: Search,    tone: "bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300" },
  traffic:    { label: "Traffic",   Icon: Globe,     tone: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300" },
  backlinks:  { label: "Backlinks", Icon: LinkIcon,  tone: "bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300" },
  seo_audit:  { label: "Audit",     Icon: Wrench,    tone: "bg-violet-500/10 text-violet-700 border-violet-500/30 dark:text-violet-300" },
};


export default function Reports() {
  const [kindFilter, setKindFilter] = useState<ReportKind | "all">("all");
  const [propertyFilter, setPropertyFilter] = useState<number | "all">("all");
  const [search, setSearch] = useState("");

  const list = useReports({
    limit: 100,
    kind: kindFilter === "all" ? null : kindFilter,
    property_id: propertyFilter === "all" ? null : propertyFilter,
  });
  const props = useProperties();

  // Build a property_id → Property lookup so each row can show the
  // property domain instead of the bare id. Cheap derived map.
  const propsById = useMemo(() => {
    const m = new Map<number, Property>();
    for (const p of props.data?.items ?? []) m.set(p.id, p);
    return m;
  }, [props.data]);

  // Client-side title/customer filter (the API doesn't have full-text
  // search; small enough to be cheap on the client). Applies AFTER the
  // server-side kind/property filters.
  const filtered = useMemo(() => {
    const items = list.data?.items ?? [];
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.customer.toLowerCase().includes(q),
    );
  }, [list.data, search]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
        <p className="text-sm text-muted-foreground">
          Customer-facing PDF reports across all properties. Generate
          new reports from each property's data pages — the kind is
          implied by the page (Crawl detail → Crawl Report, Search
          Console → Traffic Report, Backlinks → Backlinks Report).
        </p>
      </div>

      {/* Filter strip — kind pills + property dropdown + free-text search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {(Object.keys(KIND_META) as Array<ReportKind | "all">).map((k) => {
            const meta = KIND_META[k];
            const Icon = meta.Icon;
            const active = kindFilter === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setKindFilter(k)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium border transition-colors",
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background hover:bg-accent",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {meta.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <select
            value={propertyFilter === "all" ? "all" : String(propertyFilter)}
            onChange={(e) =>
              setPropertyFilter(e.target.value === "all" ? "all" : Number(e.target.value))
            }
            className="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
          >
            <option value="all">All properties</option>
            {(props.data?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.domain}</option>
            ))}
          </select>
        </div>

        <input
          type="text"
          placeholder="Search title or customer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-md border border-border bg-background px-3 py-1.5 text-xs w-56"
        />

        <div className="ml-auto text-xs text-muted-foreground tabular-nums">
          {list.data && (
            <>
              {filtered.length === list.data.total
                ? `${list.data.total}`
                : `${filtered.length} of ${list.data.total}`}
              {" "}report{list.data.total === 1 ? "" : "s"}
            </>
          )}
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        {list.isLoading && (
          <div className="px-4 py-6 text-sm text-muted-foreground">Loading…</div>
        )}
        {list.data && filtered.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-sm text-muted-foreground">
            <FileText className="h-10 w-10 opacity-50" />
            <p>
              {search.trim()
                ? `No reports matching “${search}”.`
                : kindFilter === "all" && propertyFilter === "all"
                ? "No reports yet."
                : "No reports match these filters."}
            </p>
            <p className="max-w-md text-center text-xs">
              Generate one from a property's Crawl detail page, Search
              Console lens, or Backlinks lens — the kind is implied by
              the page.
            </p>
          </div>
        )}

        {list.data && filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Kind</th>
                <th className="px-4 py-3">Property</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <ReportRow key={r.id} r={r} prop={r.property_id ? propsById.get(r.property_id) ?? null : null} />
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}


function ReportRow({ r, prop }: { r: Report; prop: Property | null }) {
  const meta = KIND_META[r.report_kind] ?? KIND_META.seo_audit;
  const Icon = meta.Icon;
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30">
      <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
        {fmtDateTime(r.created_at)}
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium",
            meta.tone,
          )}
        >
          <Icon className="h-3 w-3" />
          {meta.label}
        </span>
      </td>
      <td className="px-4 py-3 text-xs">
        {prop ? (
          <Link
            to={`/p/${prop.id}/reports`}
            className="font-mono text-foreground hover:underline"
          >
            {prop.domain}
          </Link>
        ) : (
          <span className="text-muted-foreground italic">—</span>
        )}
      </td>
      <td className="px-4 py-3">{r.customer}</td>
      <td className="px-4 py-3">{r.title}</td>
      <td className="px-4 py-3 text-xs">
        {r.report_kind === "crawl" && r.crawl_id && r.property_id ? (
          <Link
            to={`/p/${r.property_id}/crawl/${r.crawl_id}`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            Crawl #{r.crawl_id}
            <ExternalLink className="h-3 w-3" />
          </Link>
        ) : r.report_kind === "traffic" && r.property_id ? (
          <Link
            to={`/p/${r.property_id}/gsc`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            Search Console
            <ExternalLink className="h-3 w-3" />
          </Link>
        ) : r.report_kind === "backlinks" && r.property_id ? (
          <Link
            to={`/p/${r.property_id}/backlinks`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            Backlinks
            <ExternalLink className="h-3 w-3" />
          </Link>
        ) : (
          <span className="text-muted-foreground italic">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">{r.period ?? "—"}</td>
      <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
        {fmtBytes(r.file_size_bytes)}
      </td>
      <td className="px-4 py-3 text-right">
        <a
          href={reportDownloadUrl(r.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <Download className="h-3.5 w-3.5" /> PDF
        </a>
      </td>
    </tr>
  );
}
